import { useEffect, useState } from 'react';
import { api, ApiError } from '../lib/api';
import type { Block, Lesson, Offer as OfferT, OffersState, PayProvider, Checkout } from '../lib/types';
import { Sheet } from '../components/Sheet';
import { Countdown } from '../components/Countdown';
import { Amount } from '../components/Currency';
import { haptic, tg } from '../lib/telegram';
import { useApp } from '../App';

/** Раздел 7. Контент выдаётся частями — в этом весь смысл прогрева. */

export function CourseScreen() {
  const { refresh, toast } = useApp();
  const [blocks, setBlocks] = useState<Block[] | null>(null);
  const [finished, setFinished] = useState(false);
  const [lesson, setLesson] = useState<Lesson | null>(null);
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [offerOpen, setOfferOpen] = useState(false);

  const load = () => api.get<{ blocks: Block[]; finished: boolean }>('/course')
    .then((r) => { setBlocks(r.blocks); setFinished(r.finished); })
    .catch(() => {});

  useEffect(() => { void load(); }, []);

  async function submit() {
    if (!lesson) return;
    setBusy(true);
    try {
      const r = await api.post<{
        ok: boolean; reason?: string; blockCompleted?: boolean; courseCompleted?: boolean;
        reward?: { coins: number; spins: number };
      }>(`/course/lessons/${lesson.id}/code`, { code: code.trim() });

      if (!r.ok) { haptic.no(); toast(r.reason ?? 'Код не подходит', 'bad'); return; }
      haptic.ok();
      setLesson(null);
      setCode('');
      if (r.courseCompleted) {
        toast(`Курс пройден: +${r.reward?.coins} коинов`);
        setOfferOpen(true);
        void api.post('/offer/track', { event: 'offer_viewed' });
      } else if (r.blockCompleted) {
        toast(`Блок закрыт: +${r.reward?.coins} коинов`);
      } else {
        toast('Урок засчитан');
      }
      await Promise.all([load(), refresh()]);
    } catch (e) {
      toast((e as ApiError).message, 'bad');
    } finally {
      setBusy(false);
    }
  }

  if (!blocks) return <div className="h-64 animate-pulse rounded-3xl bg-surface" />;
  if (blocks.length === 0) {
    return <Empty text="Курс откроется вместе с сезоном. Пока копи коины на заданиях." />;
  }

  return (
    <div className="rise pb-4">
      <p className="mb-5 text-[15px] leading-relaxed text-ash">
        Четыре блока. Самый выгодный источник коинов в приложении: за весь курс — 500 коинов
        и 20 прокрутов сверху.
      </p>

      <div className="space-y-3">
        {blocks.map((b) => <BlockCard key={b.id} block={b} onOpen={(l) => { setLesson(l); setCode(''); }} />)}
      </div>

      {finished && (
        <button className="btn-primary mt-6 w-full" onClick={() => {
          setOfferOpen(true);
          void api.post('/offer/track', { event: 'offer_viewed' });
        }}>
          Открыть предложение
        </button>
      )}

      <Sheet open={!!lesson} onClose={() => setLesson(null)} title={lesson?.title}>
        {lesson?.videoUrl && (
          <div className="mb-4 aspect-video overflow-hidden rounded-xl border border-line bg-black">
            <iframe
              src={lesson.videoUrl} title={lesson.title} className="h-full w-full"
              allow="autoplay; fullscreen; picture-in-picture" allowFullScreen
            />
          </div>
        )}
        <p className="mb-3 text-sm text-ash">В видео назовут кодовое слово — введи его, чтобы засчитать урок.</p>
        <input
          value={code} onChange={(e) => setCode(e.target.value)}
          placeholder="Кодовое слово" autoCapitalize="none"
          className="mb-3 w-full rounded-xl border border-line bg-raised px-4 py-3 text-base
                     placeholder:text-ash focus:border-turq focus:outline-none"
        />
        <button className="btn-primary w-full" disabled={!code.trim() || busy} onClick={submit}>
          {busy ? 'Проверяем…' : 'Засчитать урок'}
        </button>
      </Sheet>

      <Offer open={offerOpen} onClose={() => setOfferOpen(false)} />
    </div>
  );
}

function BlockCard({ block, onOpen }: { block: Block; onOpen: (l: Lesson) => void }) {
  const [expanded, setExpanded] = useState(block.isOpen && !block.completedAt);
  const done = !!block.completedAt;

  if (!block.isOpen) {
    return (
      <div className="card flex items-center gap-3 p-4 opacity-70">
        <Lock />
        <div className="min-w-0 flex-1">
          <p className="text-[15px] font-semibold">Блок {block.number}</p>
          <p className="mt-0.5 text-xs text-ash">
            {block.unlocksAt
              ? <>Откроется через <Countdown to={block.unlocksAt} className="text-chalk" /></>
              : 'Откроется после предыдущего блока'}
          </p>
        </div>
      </div>
    );
  }

  return (
    <section className="card overflow-hidden">
      <button className="flex w-full items-center gap-3 p-4 text-left"
        onClick={() => { haptic.tap(); setExpanded(!expanded); }}>
        <span className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full border font-display text-sm
          ${done ? 'border-turq/50 bg-turq/10 text-turq' : 'border-gold/50 bg-gold/10 text-gold'}`}>
          {done ? '✓' : block.number}
        </span>
        <span className="min-w-0 flex-1">
          <span className="block truncate text-[15px] font-semibold">{block.title}</span>
          <span className="mt-0.5 flex items-center gap-3 text-xs">
            <span className="text-ash">
              {block.lessons.filter((l) => l.isCompleted).length}/{block.lessons.length} уроков
            </span>
            {!done && <Amount kind="coin" value={block.rewardCoins} size={12} />}
          </span>
        </span>
        <span className={`text-ash transition ${expanded ? 'rotate-180' : ''}`}>⌄</span>
      </button>

      {expanded && (
        <ul className="border-t border-line">
          {block.lessons.map((l) => (
            <li key={l.id}>
              <button
                disabled={!l.isOpen}
                onClick={() => { haptic.tap(); onOpen(l); }}
                className="flex w-full items-center gap-3 px-4 py-3 text-left disabled:opacity-45"
              >
                <span className={`h-2 w-2 shrink-0 rounded-full ${l.isCompleted ? 'bg-turq' : 'bg-line'}`} />
                <span className="min-w-0 flex-1 truncate text-sm">{l.title}</span>
                {l.durationSec && (
                  <span className="num shrink-0 text-xs text-ash">{Math.round(l.durationSec / 60)} мин</span>
                )}
              </button>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

/** Подписи рельс. Человеку важно не название сервиса, а чем он платит. */
const RAIL: Record<PayProvider, string> = {
  lava: 'Картой',
  cryptocloud: 'Криптой',
  stars: 'Звёздами',
};

function Offer({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { toast } = useApp();
  const [state, setState] = useState<OffersState | null>(null);
  const [busy, setBusy] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    void api.post('/offer/track', { event: 'offer_viewed' });
    api.get<OffersState>('/offers').then(setState).catch(() => setState({ offers: [], access: null }));
  }, [open]);

  async function pay(offer: OfferT, provider: PayProvider) {
    setBusy(`${offer.code}:${provider}`);
    try {
      void api.post('/offer/click', { event: 'offer_cta_clicked' });
      const res = await api.post<Checkout>('/offers/checkout', { offer: offer.code, provider });
      haptic.tap();

      // Звёзды платятся внутри Telegram, всё остальное — во внешнем браузере.
      // Уводить человека из вебвью без нужды нельзя: половина не возвращается.
      if (provider === 'stars' && tg.openInvoice) {
        tg.openInvoice(res.url, (status) => {
          if (status === 'paid') toast('Оплата прошла. Ссылка придёт в чат с ботом.');
        });
      } else {
        tg.openLink(res.url);
        toast('Открыли оплату. После неё ссылка придёт в чат с ботом.');
      }
    } catch (e) {
      haptic.no();
      toast(e instanceof ApiError ? e.message : 'Не получилось открыть оплату');
    } finally {
      setBusy(null);
    }
  }

  const active = state?.access;

  return (
    <Sheet open={open} onClose={onClose} title="Закрытый канал">
      {active && (
        <p className="mb-4 rounded-2xl bg-turq/10 p-3 text-sm text-turq">
          Доступ уже открыт{active.expires_at
            ? ` до ${new Date(active.expires_at).toLocaleDateString('ru-RU')}`
            : ''}. Продлить можно в любой момент.
        </p>
      )}

      <p className="mb-4 text-sm leading-relaxed text-ash">
        Курс показал систему целиком. В закрытом канале — разборы, шаблоны и ответы на вопросы
        каждую неделю.
      </p>

      {state === null && <p className="text-sm text-ash">Загружаем…</p>}

      {state?.offers.length === 0 && (
        <p className="text-sm text-ash">Оплата пока не подключена. Загляни позже.</p>
      )}

      <div className="space-y-3">
        {state?.offers.map((o) => (
          <div key={o.code} className="card p-4">
            <div className="mb-1 flex items-baseline justify-between gap-3">
              <span className="font-display">{o.title}</span>
              <span className="shrink-0 font-mono">{o.priceRub.toLocaleString('ru-RU')} ₽</span>
            </div>
            {o.description && <p className="mb-3 text-sm text-ash">{o.description}</p>}

            <div className="flex flex-wrap gap-2">
              {o.providers.map((p) => (
                <button
                  key={p}
                  className="btn-primary flex-1 whitespace-nowrap px-3 py-2 text-sm"
                  disabled={busy !== null}
                  onClick={() => void pay(o, p)}
                >
                  {busy === `${o.code}:${p}` ? '…' : RAIL[p]}
                  {p === 'stars' && o.priceStars ? ` · ${o.priceStars} ★` : ''}
                </button>
              ))}
            </div>

            {o.providers.includes('stars') && (
              <p className="mt-2 text-xs text-ash">
                Звёздами дороже: Telegram удерживает часть при выводе.
              </p>
            )}
          </div>
        ))}
      </div>
    </Sheet>
  );
}

function Lock() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" className="shrink-0" aria-hidden>
      <rect x="4.5" y="10" width="15" height="10" rx="2.5" stroke="var(--ash)" strokeWidth="1.7" />
      <path d="M8 10V7.5a4 4 0 1 1 8 0V10" stroke="var(--ash)" strokeWidth="1.7" strokeLinecap="round" />
    </svg>
  );
}

function Empty({ text }: { text: string }) {
  return <div className="card p-6 text-center text-sm text-ash">{text}</div>;
}
